import { createClient } from "@supabase/supabase-js";

const getSupabaseConfig = () => {
  if (process.env.NODE_ENV === "development") {
    return {
      url: process.env.REACT_APP_SUPABASE_URL, // From get_config.sh
      key: process.env.REACT_APP_SUPABASE_KEY, // From get_config.sh
      redirectUrl: "http://localhost:3000/auth/callback",
      updateUrl: "http://localhost:3000/auth/update",
    };
  }

  return {
    url: process.env.REACT_APP_SUPABASE_URL,
    key: process.env.REACT_APP_SUPABASE_KEY,
    redirectUrl: "https://app.paribus.ai/auth/callback",
    updateUrl: "https://app.paribus.ai/auth/update",
  };
};

const {
  url: supabaseUrl,
  key: supabaseKey,
  redirectUrl,
  updateUrl,
} = getSupabaseConfig();

export const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    redirectTo: redirectUrl,
  },
  realtime: true,
});

export const signUp = async (email, password) => {
  console.log("Attempting sign up for:", email);
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      emailRedirectTo: redirectUrl,
    },
  });
  if (error) {
    console.error("Sign up error:", error);
    return { user: null, error };
  } else {
    console.log("Sign up successful:", data);
    return {
      user: data.user,
      session: data.session,
      needsEmailConfirmation: !data.session, // If there's no session, email confirmation is needed
    };
  }
};

export const signIn = async (email, password) => {
  try {
    console.log("[Supabase] Attempting sign in");
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error("[Supabase] Sign in error:", error);
        return { user: null, session: null, error };
      }

      if (data?.session) {
        supabase.auth.setSession(data.session);
      }

      console.log("[Supabase] Session:", data?.session);

      console.log("[Supabase] Sign in successful:", {
        hasUser: !!data?.user,
        hasSession: !!data?.session,
      });

      return {
        user: data?.user,
        session: data?.session,
        error: null,
      };
    } catch (innerError) {
      console.error("[Supabase] Error during auth call:", innerError);
      return { user: null, session: null, error: innerError };
    }
  } catch (error) {
    console.error("[Supabase] Unexpected error during sign in:", error);
    return { user: null, session: null, error };
  }
};

export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  return { error };
};

export const getCurrentUser = async () => {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  return user;
};

export const signInWithMagicLink = async (email) => {
  const { data, error } = await supabase.auth.signInWithOtp({
    email,
    options: {
      emailRedirectTo: redirectUrl,
    },
  });
  return { data, error };
};

export const signInWithAzure = async () => {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: "azure",
    options: {
      scopes: "email offline_access",
      redirectTo: redirectUrl,
    },
  });
  return { data, error };
};

export const resetPasswordForEmail = async (email) => {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: updateUrl,
  });
  if (error) {
    console.error("[Supabase] Password reset error:", error);
    throw error;
  }
  return;
};

export const updatePassword = async (new_password) => {
  const { data, error } = await supabase.auth.updateUser({
    password: new_password,
  });
  if (error) {
    console.error("[Supabase] Update Password error:", error);
    throw error;
  }
  return { user: data?.user };
};

export const verifyBetaCode = async (code) => {
  // TODO move codes to secret manager
  const validCodes = [
    "TLU24",
    "TLUPARIBUS24",
    "TLUPARIBUS2024",
    "TLUPARABUS24",
  ];
  return validCodes.includes(code.toUpperCase());
};

export const addToWaitlist = async (email) => {
  const { data, error } = await supabase.from("waitlist").insert([{ email }]);

  if (error) throw error;
  return data;
};

export const generateSignedUrl = async (
  bucketName,
  filePath,
  expiresIn = 60
) => {
  try {
    const { data, error } = await supabase.storage
      .from(bucketName)
      .createSignedUrl(filePath, expiresIn);

    if (error) {
      console.error("Supabase signed URL error:", error);
      throw error;
    }

    // In development, return the direct Supabase URL
    if (process.env.NODE_ENV === "development") {
      return data.signedUrl;
    }

    // In production, use CloudFront
    const signedUrl = data.signedUrl;
    const urlObj = new URL(signedUrl);
    const path = urlObj.pathname.replace("/storage/v1/object/sign/", "");
    const token = urlObj.searchParams.get("token");

    return `https://${process.env.REACT_APP_CLOUDFRONT_DOMAIN}/${path}?token=${token}`;
  } catch (error) {
    console.error("Error generating signed URL:", error);
    return null;
  }
};

export const generateSignedUrlForDownload = async (
  bucketName,
  filePath,
  expiresIn = 60
) => {
  try {
    const { data, error } = await supabase.storage
      .from(bucketName)
      .createSignedUrl(filePath, expiresIn, {
        download: true,
        transform: {
          disposition: "attachment",
          contentType: "application/pdf",
        },
      });

    if (error) {
      console.error("Supabase signed URL error:", error);
      return null;
    }

    return data?.signedUrl;
  } catch (error) {
    console.error("Error generating signed URL for download:", error);
    return null;
  }
};

export const getTemplateName = async (templateId) => {
  try {
    const { data, error } = await supabase
      .from("templates")
      .select("name")
      .eq("id", templateId)
      .single();

    if (error) throw error;
    return data?.name;
  } catch (error) {
    console.error("Error fetching template name:", error);
    return null;
  }
};

export default supabase;
